/**
 * Sentry browser bootstrap — Phase 6.
 *
 * Single entry point for Sentry initialization. Called once from
 * main.tsx before the React tree mounts. Gated on:
 *   - VITE_SENTRY_DSN being set (otherwise nothing to talk to)
 *   - VITE_APP_ENV being something other than 'development'
 *     (so a developer doesn't fill the Sentry org with local noise)
 *
 * Provider lock-in (architect Phase 6 response):
 *   Sentry = application exception capture + safe breadcrumbs ONLY.
 *   New Relic via OpenTelemetry is the selected future provider for
 *   performance, traces, metrics, logs, and platform observability.
 *
 *   Phase 6 explicitly does NOT enable Sentry performance/tracing.
 *   The init below intentionally omits `tracesSampleRate` and any
 *   tracing integration. The matching test (`sentry-bootstrap.test.ts`)
 *   pins this — re-adding tracing config here without re-opening the
 *   provider decision will fail CI.
 *
 * Per FRONTEND_OBSERVABILITY_SECURITY.md: do NOT capture expected
 * errors (validation, auth, 403 permission, 404, 429 rate-limit)
 * as Sentry exceptions. Those flow through normal UX paths (toasts
 * + denied states + the existing 401 refresh-once path).
 */

import * as Sentry from '@sentry/react';

interface SentryBootstrapEnv {
  VITE_SENTRY_DSN?: string;
  VITE_APP_ENV?: string;
  VITE_APP_VERSION?: string;
}

let initialized = false;

function sanitizeEventUrl(value: string | undefined): string | undefined {
  if (!value) return value;
  try {
    return new URL(value, window.location.origin).pathname || '/';
  } catch {
    return value.split(/[?#]/, 1)[0] || '/';
  }
}

/**
 * Initialize Sentry. Idempotent — safe to call from main.tsx and
 * also indirectly from a test bootstrap.
 *
 * Returns true when init actually fired, false when gated off.
 * Tests use the return value to assert the gating contract.
 */
export function initSentry(
  env: SentryBootstrapEnv = import.meta.env as unknown as SentryBootstrapEnv,
): boolean {
  if (initialized) return true;
  if (!env.VITE_SENTRY_DSN) return false;
  if (env.VITE_APP_ENV === 'development') return false;

  // Error capture only. NO `tracesSampleRate`, NO tracing
  // integrations — performance/traces are New Relic/OpenTelemetry's
  // lane (provider lock-in). The matching test pins the absence.
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_APP_ENV ?? 'production',
    release: env.VITE_APP_VERSION,
    // No PII by default. Tags only carry safe internal IDs (route
    // pattern, requestId, etc.) and are set per-event via the
    // ErrorBoundary / apiClient breadcrumb code.
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request) {
        event.request.url = sanitizeEventUrl(event.request.url);
        event.request.headers = Object.fromEntries(
          Object.entries(event.request.headers ?? {}).filter(([name]) => (
            !['authorization', 'cookie', 'set-cookie', 'x-api-key', 'x-forwarded-for', 'x-real-ip', 'user-agent']
              .includes(name.toLowerCase())
          )),
        );
      }
      return event;
    },
  });
  initialized = true;
  return true;
}

/**
 * Test-only — clears the bootstrap latch so a follow-up test can
 * re-exercise the gating logic.
 */
export function __resetSentryForTests(): void {
  initialized = false;
}
